import { LinkOffOutlined, LinkOutlined } from '@vindr/icons';
// import { useGetDevices } from '@vindr/services/actions/devices.action';
// import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useGetStudiesIntervalV2 } from '@vindr/services/actions/study.action';
import { STATUS_COLOR } from '@vindr/services/constants/common.constant';
import {
  CAD_STATUS,
  MATCH_STATUS,
  STUDYLIST_COLUMNS_ORIGINAL,
  STUDYLIST_STATUS,
} from '@vindr/services/constants/study.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import ColumnTemplate from '@vindr/ui/src/components/Templates/columns/columnRenderTemplates';
import { PaginationProps } from '@vindr/ui/src/components/ui/Table';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { Pick, getArrayVal } from '@vindr/utils/helpers/common.helper';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import { displayText } from '@vindr/utils/index';
import { Checkbox, Tag, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface StudylistColumnsType
  extends DefinedColumns<ReturnType<typeof useGetStudiesIntervalV2>['data']['data'][number]> {}

const defaultColumns = [
  'index',
  // 'match_status',
  'patient_id',
  'patient_name',
  // 'patient_birth_date',
  // 'patient_sex',
  // 'studylist_status',
  'modality',
  'study_date',
  'study_desc',
  // 'diagnostic',
];

const useDefinedColumns = (pagination: PaginationProps): StudylistColumnsType[] => {
  const { t } = useTranslation('Vindoc');

  // const { data: devices, isInitialLoading: devicesLoading } = useGetDevices({
  //   limit: 100,
  //   offset: 0,
  // });
  // const { data: institutions, isInitialLoading: institutionLoading } = useGetInstitutions({
  //   limit: 100,
  //   offset: 0,
  // });

  const definedColumns = [
    ColumnTemplate.Index({
      pagination,
      key: 'index',
      dataIndex: 'index',
      width: 60,
    }),
    {
      title: t('Patient ID'),
      width: 140,
      dataIndex: 'mrn',
      key: 'patient_id',
      ellipsis: true,
    },
    {
      title: t('Patient Name'),
      width: 150,
      dataIndex: 'patientName',
      key: 'patient_name',
      ellipsis: true,
    },
    {
      title: t('Age'),
      dataIndex: 'patient_age',
      key: 'patient_age',
      width: 80,
      align: 'center',
      ellipsis: true,
    },
    ColumnTemplate.Date({
      title: t('Date of Birth'),
      dataIndex: 'patient_birth_date',
      key: 'patient_birth_date',
      showTime: false,
      sorter: false,
      width: 110,
    }),
    ColumnTemplate.Gender({
      key: 'patient_sex',
      dataIndex: 'patient_sex',
      width: 60,
    } as StudylistColumnsType),
    {
      title: t('Status'),
      width: 80,
      dataIndex: 'studylist_status',
      key: 'studylist_status',
      ellipsis: true,
      render: (status) => (
        <Tag
          color={STATUS_COLOR[STUDYLIST_STATUS[status]]}
          className="flex w-full justify-center align-middle"
        >
          {t(STUDYLIST_STATUS[status])}
        </Tag>
      ),
    },
    {
      title: t('Study Description'),
      dataIndex: 'description',
      key: 'study_desc',
      width: 300,
      ellipsis: true,
    },
    {
      title: t('Study Date'),
      dataIndex: 'study_date',
      key: 'study_date',
      width: 140,
      sorter: true,
      defaultSortOrder: 'descend',
      ellipsis: true,
      render: (date, record) => formatDateColumn(record.date + ' ' + record.time),
    },
    // {
    //   title: t('Order Group'),
    //   dataIndex: 'order_group',
    //   key: 'order_group',
    //   width: 120,
    //   ellipsis: true,
    //   render: (text) => displayText(text),
    // },
    // {
    //   title: t('Body Part'),
    //   dataIndex: 'body_part',
    //   key: 'body_part',
    //   width: 120,
    //   ellipsis: true,
    // },
    // {
    //   title: t('Diagnoses'),
    //   dataIndex: 'diagnostic',
    //   key: 'diagnostic',
    //   width: 120,
    //   ellipsis: true,
    //   render: (text) => getArrayVal(text),
    // },
    {
      title: t('Note'),
      dataIndex: 'study_comment',
      key: 'study_comment',
      width: 120,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Match'),
      width: 60,
      align: 'center',
      dataIndex: 'match_status',
      key: 'match_status',
      render: (matched) => (
        <Tooltip
          title={matched === MATCH_STATUS.Matched ? t('Matched') : t('Unmatched')}
          placement="bottom"
          overlayClassName="toolbar-tooltip"
        >
          <span className="flex items-center justify-center">
            {matched === MATCH_STATUS.Matched ? (
              <LinkOutlined className="text-lg text-primary-light" />
            ) : (
              <LinkOffOutlined className="text-lg" />
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('CAD'),
      width: 60,
      align: 'center',
      dataIndex: 'abnormal_status',
      key: 'abnormal_status',
      // hide: !cadEnabled,
      render: (status) => (
        <Tooltip
          title={
            status === CAD_STATUS.NONE ? (
              t('Unavailable CAD')
            ) : (
              <>
                <span>{status === CAD_STATUS.ABNORMAL ? t('Abnormal') : t('Normal')}</span>
                <span style={{ color: '#ffffffa6' }}> [{t('CAD Suggestion')}]</span>
              </>
            )
          }
          placement="bottom"
          overlayClassName="toolbar-tooltip"
        >
          <div
            className="m-auto h-2 w-2 rounded"
            style={{
              backgroundColor:
                status === CAD_STATUS.NONE
                  ? 'rgba(148,148,148,0.3)'
                  : status === CAD_STATUS.ABNORMAL
                  ? '#ff4a4a'
                  : '#00ac5a',
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: t('Visit ID'),
      width: 100,
      dataIndex: 'visit_id',
      key: 'visit_id',
      ellipsis: true,
    },
    {
      title: '#S/#I',
      dataIndex: 'series_count',
      key: 'series_count',
      ellipsis: true,
      width: 80,
      render: (_, record) =>
        record.series_count && record.instances_count
          ? `${record.series_count}/${record.instances_count}`
          : '',
    },
    {
      title: t('Modality'),
      dataIndex: 'modalities',
      key: 'modality',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Department'),
      dataIndex: 'department',
      key: 'department',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('Device'),
      dataIndex: 'device_id',
      key: 'device_id',
      width: 80,
      ellipsis: true,
      render: (deviceId) => {
        const device = devices?.data?.find((item) => item.id === deviceId);
        return device?.name;
      },
    },
    {
      title: 'DICOM',
      dataIndex: 'is_dicom',
      key: 'is_dicom',
      align: 'center',
      width: 80,
      ellipsis: true,
      render: (isDicom) => {
        return (
          <Checkbox
            checked={isDicom}
            disabled
            style={{ pointerEvents: 'none' }}
            className="text-primary-light"
          />
        );
      },
    },
    {
      title: t('Order Doctor Name'),
      dataIndex: 'order_doctor_name',
      key: 'order_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Procedure Code'),
      dataIndex: 'procedure_code',
      key: 'procedure_code',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Procedure Name'),
      dataIndex: 'procedure_name',
      key: 'procedure_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Referring Physician'),
      dataIndex: 'refer_doctor_name',
      key: 'refer_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Study UID'),
      dataIndex: 'study_instance_uid',
      key: 'study_instance_uid',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('Study Status'),
      dataIndex: 'study_status',
      key: 'study_status',
      width: 80,
      ellipsis: true,
      render: (status) => (
        <Tag
          color={STATUS_COLOR[STUDYLIST_STATUS[status]]}
          className="flex w-full justify-center align-middle"
        >
          {t(STUDYLIST_STATUS[status])}
        </Tag>
      ),
    },
    {
      title: t('Institution'),
      dataIndex: 'institution_id',
      key: 'institution_id',
      width: 80,
      ellipsis: true,
      render: (institutionId) => {
        const institution = institutions?.data?.find((i) => i.id === institutionId);
        return institution?.name;
      },
    },
    {
      title: t('Study ID'),
      dataIndex: 'study_id',
      key: 'study_id',
      width: 140,
      ellipsis: true,
    },
  ];

  const indexSelectedColumns = Array.isArray(window?.config?.columns?.studylist)
    ? window?.config?.columns?.studylist
    : defaultColumns;

  const sorted = definedColumns.sort((a, b) => {
    const indexA = indexSelectedColumns.indexOf(a.key);
    const indexB = indexSelectedColumns.indexOf(b.key);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const columnDefault = [];

  for (const col of indexSelectedColumns) {
    const column = definedColumns.find((item) => item.key === col);
    if (column) columnDefault.push(Pick(column, ['title', 'key', 'dataIndex', 'width']));
  }
  localStorageAction.set(STUDYLIST_COLUMNS_ORIGINAL, { resizableColumns: columnDefault });

  return useMemo(() => sorted, [t]);
};

export default useDefinedColumns;
